import { Grid } from '@mui/material';
import { HSpacer, RoutalTooltip, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import StopCanceledFields from '../../../../../assets/svg/right-panel-tab-icons/StopCanceledFields';
import StopCompletedFields from '../../../../../assets/svg/right-panel-tab-icons/StopCompletedFields';
import StopFields from '../../../../../assets/svg/right-panel-tab-icons/StopFields';
import StopSurveyFields from '../../../../../assets/svg/right-panel-tab-icons/StopSurveyFields';
import { RenderInputPhone, renderInputText } from '../../../../../components/right-panel-info/Common';
import { InfoContainer } from '../../../../../components/right-panel-info/RightPanel.styled';
import { Project } from '../../../../../domain/project/Project';
import { Supervisor } from '../../../../../domain/supervisor/Supervisor';
import { intl } from '../../../../../helpers/IntGlobalProvider';
import { useRetrieveSupervisorRightPanelInfo } from '../../../../../hooks/right-panel/useRetrieveSupervisorRightPanelInfo';
import { useUpdateSupervisor } from '../../../../../hooks/supervisor/useUpdateSupervisor';
import Text from '../../../../../react-components/Text';
import { SupervisorStopCanceledFields } from './SupervisorStopCanceledFields';
import { SupervisorStopCompletedFields } from './SupervisorStopCompletedFields';
import { SupervisorStopFields } from './SupervisorStopFields';
import { SupervisorStopSurveyFields } from './SupervisorStopSurveyFields';

interface SupervisorInfoSectionProps {
  supervisor: Supervisor;
  section: number;
  project: Project;
}

const SupervisorInfoSection = ({ supervisor, section, project }: SupervisorInfoSectionProps) => {
  switch (section) {
    case 0:
      return (
        <div style={{ height: `100%`, overflowY: `auto`, minHeight: `300px` }}>
          <SupervisorStopFields supervisor={supervisor} project={project} />
          <VSpacer large />
        </div>
      );
    case 1:
      return (
        <div style={{ height: `100%`, overflowY: `auto`, minHeight: `300px` }}>
          <SupervisorStopCompletedFields supervisor={supervisor} project={project} />
          <VSpacer large />
        </div>
      );

    case 2:
      return (
        <div style={{ height: `100%`, overflowY: `auto`, minHeight: `300px` }}>
          <SupervisorStopCanceledFields supervisor={supervisor} project={project} />
          <VSpacer large />
        </div>
      );
    case 3:
      if (!project.customers.fields.find((field) => field.id === `survey`)) return null;

      return (
        <div style={{ height: `100%`, overflowY: `auto`, minHeight: `300px` }}>
          <SupervisorStopSurveyFields supervisor={supervisor} project={project} />
          <VSpacer small />
        </div>
      );
    default:
      return null;
  }
};

interface SupervisorProps {
  supervisor: Supervisor;
  activeProject: Project;
}

export const SupervisorBody = ({ supervisor, activeProject }: SupervisorProps) => {
  const { run: updateSupervisor } = useUpdateSupervisor();

  const { detailTab, setRightPanelInfo } = useRetrieveSupervisorRightPanelInfo();

  const [infoSection, setInfoSection] = useState<number>(0);

  useEffect(() => {
    switch (detailTab) {
      case `stop_completed_fields`:
        setInfoSection(1);
        break;
      case `stop_canceled_fields`:
        setInfoSection(2);
        break;
      case `survey_fields`:
        if (activeProject.customers.fields.find((field) => field.id === `survey`)) {
          setInfoSection(3);
        } else {
          setInfoSection(0);
        }
        break;
      default:
      case `stop_fields`:
        setInfoSection(0);
        setInfoSection(0);
        break;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailTab]);

  const handleUpdate = (label: string, val: any) => {
    updateSupervisor({ supervisor, changes: { [label]: val } });
  };

  return (
    <Grid container direction="column" wrap="nowrap" style={{ height: `100%` }}>
      <InfoContainer>
        {renderInputText({
          placeholderIntlId: `supervisors.data.details.name`,
          variant: `clean`,
          property: `name`,
          size: `title`,
          handleUpdate,
          data: supervisor,
        })}

        <VSpacer small />

        {renderInputText({
          labelIntlId: `supervisors.data.details.email`,
          variant: `outlined`,
          property: `email`,
          type: `email`,
          handleUpdate,
          data: supervisor,
        })}

        <VSpacer small />

        <Text
          intlId="supervisors.data.details.company"
          variant="label"
          style={{ cursor: `default`, marginBottom: `8px` }}
        />
        {renderInputText({
          placeholderIntlId: `supervisors.data.details.company`,
          variant: `outlined`,
          property: `company`,
          handleUpdate,
          data: supervisor,
        })}

        <VSpacer small />

        <Text
          intlId="supervisors.data.details.short_id"
          variant="label"
          style={{ cursor: `default`, marginBottom: `8px` }}
        />
        {renderInputText({
          placeholderIntlId: `supervisors.data.details.short_id`,
          variant: `outlined`,
          property: `short_id`,
          handleUpdate,
          data: supervisor,
        })}
        <VSpacer small />

        <RenderInputPhone
          data={supervisor}
          defaultCountry={
            activeProject?.location?.country_code ? activeProject?.location?.country_code.toUpperCase() : undefined
          }
          property="phone"
          handleUpdate={handleUpdate}
          labelIntlId="supervisors.data.details.phone"
          placeholderIntlId="supervisors.data.details.phone"
        />
        <VSpacer small />
      </InfoContainer>

      <Grid
        container
        direction="row"
        alignItems="center"
        justifyContent="center"
        style={{
          minHeight: `60px`,
          borderTop: `1px solid #CACACA`,
          borderBottom: `1px solid #CACACA`,
        }}
      >
        <RoutalTooltip
          key="supervisor-stop_fields"
          title={intl.formatMessage({ id: `properties.supervisor.stop_fields` })}
        >
          <StopFields
            isSelected={infoSection === 0}
            style={{
              cursor: infoSection === 0 ? `default` : `pointer`,
            }}
            onClick={() => {
              setRightPanelInfo({ detailTab: `stop_fields` });
            }}
          />
        </RoutalTooltip>

        <HSpacer large />

        <RoutalTooltip
          key="supervisor-stop_completed_fields"
          title={intl.formatMessage({ id: `properties.supervisor.stop_completed_fields` })}
        >
          <StopCompletedFields
            isSelected={infoSection === 1}
            style={{
              cursor: infoSection === 1 ? `default` : `pointer`,
            }}
            onClick={() => {
              setRightPanelInfo({ detailTab: `stop_completed_fields` });
            }}
          />
        </RoutalTooltip>

        <HSpacer large />

        <RoutalTooltip
          key="supervisor-stop_canceled_fields"
          title={intl.formatMessage({ id: `properties.supervisor.stop_canceled_fields` })}
        >
          <StopCanceledFields
            isSelected={infoSection === 2}
            style={{
              cursor: infoSection === 2 ? `default` : `pointer`,
            }}
            onClick={() => {
              setRightPanelInfo({ detailTab: `stop_canceled_fields` });
            }}
          />
        </RoutalTooltip>

        {activeProject.customers.fields.find((field) => field.id === `survey`) ? (
          <>
            <HSpacer large />

            <RoutalTooltip
              key="supervisor-survey_fields"
              title={intl.formatMessage({ id: `properties.supervisor.survey_fields` })}
            >
              <StopSurveyFields
                isSelected={infoSection === 3}
                style={{
                  cursor: infoSection === 3 ? `default` : `pointer`,
                }}
                onClick={() => {
                  setRightPanelInfo({ detailTab: `survey_fields` });
                }}
              />
            </RoutalTooltip>
          </>
        ) : null}
      </Grid>

      <SupervisorInfoSection supervisor={supervisor} section={infoSection} project={activeProject} />
    </Grid>
  );
};
